import _ from 'lodash'
import Vue from 'vue'
import { getEntryFields } from '_/components/Admin/Admin.fields'
import { getObjectPropertyValueByPath } from '_/helpers/lodashHelper'
import { mapGetters } from 'vuex'
import { formatBytes } from '_/helpers/formatBytes'
import { store } from '_/vuex/store'
import { eventBus } from '_/helpers/eventBus'
const photonConfig = require('~/config/config.json')
import { api } from '_/services/api'
import { pError } from '_/helpers/logger'

const ActionButton = Vue.component(
  'ActionButton',
  require('_/components/UserInterface/ActionButton/ActionButton.vue').default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    disableDelete: {
      default: false,
      type: Boolean,
    },
    disableCreateAnother: {
      default: false,
      type: Boolean,
    },
    registeredModuleName: {
      type: String,
      required: true,
    },
    updateAssetEditor: {
      type: [Number, String],
    },
  },

  /**
   * Setup the beforeCreate hook
   *
   * @return  {void}
   */
  beforeCreate() {
    /**
     * Then EntryForm component is loaded recursively, and the proposed method that suggests that it's OK to have
     * recursive components as long as you have a unique name for each failed.
     * Evan suggested this undocumented solution in https://github.com/vuejs/vue/issues/4117 to fix the issue.
     */
    this.$options.components.EntryForm = require('_/components/UserInterface/EntryForm/EntryForm.vue').default
  },

  /**
   * Set the component data
   *
   * @return  {object}
   */
  data: function() {
    return {
      /**
       * Stores the entry fields configuration object
       *
       * @type  {Array}
       */
      fields: [],

      /**
       * An object sent to child components; used to signal that the fields need to be reset, with the ability
       * to limit the reset to only certain fields
       *
       * @type  {Object}
       */
      formFieldsReset: {
        /**
         * A list of field names to include in a reset action
         *
         * @type  {Array}
         */
        includeFields: [],

        /**
         * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
         * set the fresh value.
         *
         * @type  {integer}
         */
        resetData: null,
      },

      /**
       * More info section visibility
       *
       * @type  {boolean}
       */
      isMoreInfoVisible: false,
    }
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
      ui: 'ui/ui',
    }),

    /**
     * Map the assetManager model as admin
     *
     * @return  {object}
     */
    admin() {
      return this.assetsManager
    },

    /**
     * Define the assetsManager getter
     *
     * @return  {object}
     */
    assetsManager() {
      const getterName = `${this.registeredModuleName}/${this.registeredModuleName}`

      return this.$store.getters[getterName]
    },

    /**
     * Created at getter
     *
     * @return  {string}
     */
    createdAt() {
      return _.has(this.admin.editedEntry, 'system.created_at')
        ? moment(_.get(this.admin.editedEntry, 'system.created_at')).format(
            this.dateFormat
          )
        : null
    },

    /**
     * Published at getter
     *
     * @return  {string}
     */
    publishedAt() {
      return _.get(this.admin.editedEntry, 'system.published_at')
    },

    /**
     * Returns the published state
     * @returns boolean
     */
    isAlreadyPublished() {
      const date_time = moment().format('YYYY-MM-DD HH:mm:ss')
      return moment(date_time).isSameOrAfter(
        _.get(this.admin.editedEntry, 'system.published_at')
      )
    },

    /**
     * Gets the published date text
     */
    schedulePublishDateText() {
      if (this.isAlreadyPublished) {
        return this.$t('admin.publishedAt')
      } else {
        return this.$t('admin.scheduledAt')
      }
    },

    /**
     * Date format getter
     *
     * @return  {string}
     */
    dateFormat() {
      return this.ui.photonConfig.dateFormat
    },

    /**
     * Checks if this.fields is populated
     *
     * @return  {Boolean}
     */
    hasFields() {
      return !_.isEmpty(this.fields)
    },

    /**
     * Returns true if there's an asset selected
     *
     * @return  {Boolean}
     */
    isAssetSelected() {
      return _.has(this.assetsManager.editedEntry, 'system.id')
    },

    /**
     * Gets the scheduled publishing modal name
     *
     * @return  {string}
     */
    schedulePublishingModalName() {
      return 'schedule-publishing-' + this.registeredModuleName
    },

    /**
     * Updated at getter
     *
     * @return  {string}
     */
    updatedAt() {
      return _.has(this.admin.editedEntry, 'system.updated_at')
        ? moment(_.get(this.admin.editedEntry, 'system.updated_at')).format(
            this.dateFormat
          )
        : null
    },
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Prepares the payload object and runs the deleteEntry method for dropdown button
     *
     * {boolean}  options.force
     * @return  {promise}
     */
    deleteEntryDropdown({ force = false } = {}) {
      let confirmed = confirm(this.$t('admin.confirmDelete'))
      if (confirmed === true) {
        store.dispatch(`${this.assetsManager.moduleName}/confirmDeleteEntry`)
        eventBus.$emit('deleteEntryConfirmed')
      }

      this.deleteEntry({
        entryId: _.get(this.admin.editedEntry, 'system.id'),
        selectedModuleName: this.admin.selectedModule.table_name,
        force,
      })
    },

    /**
     * Prepares the payload object and runs the deleteEntry method
     *
     * {boolean}  options.force
     * @return  {promise}
     */
    deleteEntryConfirmed({ force = false } = {}) {
      eventBus.$emit('deleteEntryConfirmed')

      return this.deleteEntry({
        entryId: _.get(this.admin.editedEntry, 'system.id'),
        selectedModuleName: this.admin.selectedModule.table_name,
        force,
      })
    },

    /**
     * Commits the deleteEntry action
     *
     * @param   {integer}  options.entryId
     * @param   {string}  options.selectedModuleName
     * @param   {boolean}  options.force
     * @return  {promise}
     */
    deleteEntry({ entryId, selectedModuleName, force }) {
      store.dispatch(`${this.assetsManager.moduleName}/deleteEntry`, {
        entryId,
        selectedModuleName,
        force,
      })
    },

    /**
     * Unpublish entry and save form
     *
     * @type {void}
     */
    unpublish() {
      store.dispatch(
        `${this.assetsManager.moduleName}/updateEntrySystemField`,
        {
          name: 'published_at',
          newValue: null,
        }
      )
      this.onSubmit()
    },

    schedulePublishing() {
      eventBus.$emit(
        'openSchedulePublishingModal',
        this.schedulePublishingModalName
      )
    },

    /**
     * Set published_at datetime for entry to current datetime and save form
     *
     * @type {void}
     */
    publish() {
      store.dispatch(
        `${this.assetsManager.moduleName}/updateEntrySystemField`,
        {
          name: 'published_at',
          newValue: moment().format('YYYY-MM-DD HH:mm:ss'),
        }
      )
      this.onSubmit()
    },

    /**
     * Executed when a new file was uploaded
     *
     * @param   {object}  response
     * @return  {void}
     */
    fileDuplicated(response) {
      const entry = response.body.body.entry

      if (entry) {
        store.dispatch(`${this.assetsManager.moduleName}/updateAssetsEntries`, {
          values: entry,
        })
      }

      store.dispatch(`${this.assetsManager.moduleName}/selectAsset`, {
        asset: entry,
        selectActiveAsset: false,
      })

      store.dispatch(`${this.assetsManager.moduleName}/selectActiveAsset`, {
        asset: entry,
      })
    },

    /**
     * Duplicates the entry
     *
     * @type mixed
     */
    duplicate() {
      store
        .dispatch(`${this.assetsManager.moduleName}/setSubmitEntryInProgress`, {
          value: true,
        })
        .then(() => {
          let uri =
            'duplicate/' +
            this.admin.selectedModule.table_name +
            '/' +
            this.admin.entry.system.id
          let payload = []
          api
            .post(uri, payload)
            .then(response => {
              if (
                _.has(response, 'body.body.entry.system.id') &&
                response.body.body.entry.system.id !== undefined
              ) {
                return store
                  .dispatch(
                    `${this.assetsManager.moduleName}/setSubmitEntryInProgress`,
                    {
                      value: false,
                    }
                  )
                  .then(() => {
                    this.fileDuplicated(response)
                  })
              }

              return alert(
                'Error while trying to duplicate the entry:',
                response.message
              )
            })
            .catch(response => {
              pError('Error while trying to duplicate the entry:', response)
            })
        })
    },

    /**
     * Formats byte to human readable value
     *
     * @param   {integer}  bytes
     * @return  {string}
     */
    formatBytes,

    /**
     * Gets Entry Fields, along with some field data transformations
     *
     * @return  {void}
     */
    getFields() {
      this.fields = getEntryFields(this)

      this.fields = this.fields.map(field => {
        if (field.name === 'file' && !this.assetsManager.uploadModeActive) {
          field['hidden'] = true
        }

        field.mutation = `${this.assetsManager.moduleName}/UPDATE_ASSET_MODULE_ENTRY_FIELD`

        return field
      })

      this.resetFormFields()
    },

    getObjectPropertyValueByPath,

    /**
     * Checks mime type to determine if a file is an image
     *
     * @param   {string}  mimeType
     * @return  {Boolean}
     */
    isImage(mimeType) {
      return photonConfig.allowedImageMimeTypes.indexOf(mimeType) > -1
    },

    /**
     * Submit form handler
     *
     * @type {void}
     */
    onSubmit() {
      const $form = $(`#entry-editor-form-${this.assetsManager.moduleName}`)

      $form.submit()
    },

    /**
     * Sets the formFieldsReset data property in order to force refresh of the EntryForm component
     *
     * @param   {object}  options.includeFields The names of field components that need to be included in reset
     * @return  {void}
     */
    resetFormFields({ includeFields = null } = {}) {
      this.formFieldsReset.resetData = moment().valueOf()

      this.formFieldsReset.includeFields = []

      if (includeFields) {
        this.formFieldsReset.includeFields = includeFields
      }
    },

    /**
     * Toggles the more info section visibility
     *
     * @return  {void}
     */
    toggleMoreInfo() {
      this.isMoreInfoVisible = !this.isMoreInfoVisible
    },

    /**
     * Toggles the adjust focal point visibility
     *
     * @return  {void}
     */
    toggleAdjustFocalPoint() {
      store.dispatch(`${this.registeredModuleName}/toggleAdjustFocalPoint`)
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted: function() {
    this.$nextTick(function() {
      store
        .dispatch('photonModule/getPhotonModuleInformation', {
          moduleTableName: 'assets',
        })
        .then(() => {
          const selectedModule = this.photonModule.moduleInformation.assets

          store.dispatch(
            `${this.assetsManager.moduleName}/updateSelectedModule`,
            { module: selectedModule }
          )

          this.getFields()
        })
    })
  },

  /**
   * Define the watched properties
   *
   * @type  {Object}
   */
  watch: {
    'admin.entryUpdated'() {
      this.getFields()
    },

    'admin.editorMode'(newEntry, oldEntry) {
      if (newEntry == 'create') {
        this.getFields()
      }
    },

    updateAssetEditor() {
      this.getFields()
    },
  },
}
